import type { ReactNode } from 'react';
import type {
  EligibilityCurrentResponse,
  EligibilityHistoryEntry,
} from '@features/patient-coverages/types';
import { ShieldOutlined } from '@mui/icons-material';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import { ELIG_TOKENS } from '../theme/readinessTokens';
import { coverageSourceSummary } from '../utils/coveragePresentation';

import { CoverageRecordHistory } from './CoverageRecordHistory';
import { CoverageRecordSummary } from './CoverageRecordSummary';

interface Props {
  current?: EligibilityCurrentResponse;
  coverageLabel: string;
  showHeading?: boolean;
  selector?: ReactNode;
  actions?: ReactNode;
  contextContent?: ReactNode;
  historyItems?: EligibilityHistoryEntry[];
  historyStatus?: ReactNode;
  payerHistory?: ReactNode;
}

/** Production presentation shared with stories; commands belong to the workspace. */
export function CoverageBenefitsPanel({
  current,
  coverageLabel,
  showHeading = true,
  selector,
  actions,
  contextContent,
  historyItems,
  historyStatus,
  payerHistory,
}: Props) {
  const source = current ? coverageSourceSummary(current) : undefined;
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: ELIG_TOKENS.lineStrong,
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(28, 30, 133, 0.05)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        useFlexGap
        sx={{ alignItems: 'flex-start', flexWrap: 'wrap', px: { xs: 2, md: 2.5 }, py: 2 }}
      >
        <Box
          sx={{
            bgcolor: ELIG_TOKENS.graySoft,
            color: ELIG_TOKENS.midnight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1.5,
            width: 36,
            height: 36,
            flexShrink: 0,
          }}
        >
          <ShieldOutlined fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: { xs: 180, sm: 0 } }}>
          {showHeading && (
            <Typography
              component="h3"
              sx={{ fontSize: 18, fontWeight: 780, letterSpacing: '-0.02em' }}
            >
              Eligibility
            </Typography>
          )}
          {selector ?? (
            <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 12.5, mt: 0.25 }}>
              {coverageLabel}
            </Typography>
          )}
        </Box>
        {current && source && current.state !== 'empty' && (
          <Box
            sx={{
              bgcolor:
                source.badge === 'Manual record' ? ELIG_TOKENS.greenSoft : ELIG_TOKENS.graySoft,
              color: source.badge === 'Manual record' ? ELIG_TOKENS.green : ELIG_TOKENS.gray,
              borderRadius: 999,
              fontSize: 10.5,
              fontWeight: 800,
              px: 1,
              py: 0.5,
              flexShrink: 0,
            }}
          >
            {source.badge}
          </Box>
        )}
      </Stack>
      <Divider />
      {current ? (
        <CoverageRecordSummary current={current} benefitSupplement={payerHistory}>
          {contextContent && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: ELIG_TOKENS.lineStrong,
                borderRadius: 1.5,
              }}
            >
              {contextContent}
            </Box>
          )}
          {actions && <Box sx={{ mt: 2 }}>{actions}</Box>}
          {current.state === 'empty' && (
            <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 11.5, mt: 1.4 }}>
              Recording payer information does not run or consume an automatic check.
            </Typography>
          )}
          <Divider sx={{ my: 1.75 }} />
          <CoverageRecordHistory
            items={historyItems ?? []}
            current={current}
            status={historyStatus}
          />
        </CoverageRecordSummary>
      ) : (
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2.25 }}>{payerHistory}</Box>
      )}
    </Paper>
  );
}
